import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DollarSign, Calendar, Star, TrendingUp, Landmark } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Navbar from "../components/Navbar";
import ProviderTabs from "../components/ProviderTabs";
import ProviderLoadingScreen from "../components/ProviderLoadingScreen";
import { formatDOP } from '../lib/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProviderDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetch(`${BACKEND_URL}/api/provider/dashboard`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => { setStats(data); setLoading(false); })
      .catch(() => navigate('/login'));
  }, [user, authLoading, navigate]);

  if (authLoading || loading) return <ProviderLoadingScreen skeleton />;

  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-DO';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {!user.verified && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3">
          <div className="container mx-auto max-w-7xl flex items-center gap-3">
            <span className="text-yellow-500 text-xl shrink-0">⏳</span>
            <div className="flex-1">
              <p className="font-semibold text-yellow-800 text-sm">{t('providerDashboard.pendingVerifTitle')}</p>
              <p className="text-yellow-700 text-xs mt-0.5">{t('providerDashboard.pendingVerifDesc')}</p>
            </div>
            <button
              onClick={() => navigate('/provider/verificacion')}
              className="shrink-0 px-4 py-2 rounded-full bg-yellow-500 text-white text-sm font-bold hover:bg-yellow-600 transition-colors"
            >
              {t('providerDashboard.verifyCta', 'Verificar identidad')}
            </button>
          </div>
        </div>
      )}

      <ProviderTabs />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8 pb-24 md:pb-8">
        <h1 className="text-3xl md:text-4xl font-heading font-bold mb-6 md:mb-8">{t('providerDashboard.title')}</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-6">
          {[
            { icon: DollarSign, label: t('providerDashboard.monthRevenue'), value: formatDOP(stats.monthly_revenue), color: 'bg-green-100 text-green-600' },
            { icon: Calendar, label: t('providerDashboard.monthBookings'), value: stats.bookings_this_month || 0, color: 'bg-blue-100 text-blue-600' },
            { icon: DollarSign, label: t('providerDashboard.totalRevenue'), value: formatDOP(stats.total_revenue), color: 'bg-emerald-100 text-emerald-600' },
            { icon: Star, label: t('providerDashboard.avgRating'), value: stats.avg_rating || '—', color: 'bg-yellow-100 text-yellow-600' },
          ].map((stat, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-4 md:p-6 shadow-sm">
              <div className={`w-10 h-10 md:w-12 md:h-12 ${stat.color} rounded-xl flex items-center justify-center mb-2 md:mb-3`}>
                <stat.icon className="w-5 h-5 md:w-6 md:h-6" />
              </div>
              <p className="text-xl md:text-2xl font-bold">{stat.value}</p>
              <p className="text-xs md:text-sm text-gray-500 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
          {[
            { icon: TrendingUp, label: t('providerDashboard.activeAssets'), value: stats.active_assets || 0, color: 'bg-green-100 text-green-700', action: () => navigate('/provider/assets') },
            { icon: TrendingUp, label: t('providerDashboard.inactiveAssets'), value: stats.inactive_assets || 0, color: 'bg-gray-100 text-gray-600', action: () => navigate('/provider/assets') },
            { icon: Calendar, label: t('providerDashboard.activeBookings'), value: stats.active_bookings_count || 0, color: 'bg-orange-100 text-orange-600', action: () => navigate('/provider/bookings') },
            { icon: Calendar, label: t('providerDashboard.cancelledMonth'), value: stats.cancelled_this_month || 0, color: 'bg-red-100 text-red-600', action: null },
          ].map((stat, idx) => (
            <button
              key={idx}
              onClick={stat.action || undefined}
              className={`bg-white rounded-2xl p-4 shadow-sm text-left transition-all ${stat.action ? 'hover:shadow-md active:opacity-75 cursor-pointer' : 'cursor-default'}`}
            >
              <div className={`w-10 h-10 ${stat.color} rounded-xl flex items-center justify-center mb-2`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <p className="text-2xl font-bold">{stat.value}</p>
              <p className="text-xs text-gray-500 mt-1">{stat.label}</p>
              {stat.action && <p className="text-xs text-[#134168] mt-1 font-medium">{t('providerDashboard.view')}</p>}
            </button>
          ))}
        </div>

        <button
          onClick={() => navigate('/provider/pagos')}
          className="w-full bg-white rounded-3xl p-5 md:p-6 shadow-sm mb-6 flex items-center gap-4 text-left hover:shadow-md active:opacity-75 transition-all"
        >
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${user.payout_account ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-600'}`}>
            <Landmark className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-base">{t('payout.title')}</p>
            <p className="text-sm text-gray-500 mt-0.5">
              {user.payout_account
                ? (user.payout_account.method === 'paypal'
                    ? `PayPal · ${user.payout_account.paypal_email}`
                    : `${user.payout_account.bank_name} · ****${(user.payout_account.account_number || '').slice(-4)}`)
                : t('payout.notConfigured')}
            </p>
          </div>
          <span className="text-sm text-[#134168] font-medium shrink-0">{t('providerDashboard.view')}</span>
        </button>

        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold">{t('providerDashboard.upcomingTitle')}</h2>
            <button onClick={() => navigate('/provider/bookings')} className="text-sm text-[#134168] font-medium hover:underline">{t('providerDashboard.viewAll')}</button>
          </div>
          {(stats.upcoming_bookings || []).length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t('providerDashboard.noActive')}</p>
          ) : (
            <div className="space-y-4">
              {(stats.upcoming_bookings || []).map(booking => (
                <div key={booking.booking_id} className="border border-gray-200 rounded-2xl p-4 md:p-6">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-bold text-base">{booking.booking_code}</p>
                      <p className="text-sm text-gray-600 mt-0.5">{booking.client_name}</p>
                      <p className="text-sm text-gray-500">{new Date(booking.start_datetime).toLocaleString(locale)} · {booking.end_hours}h</p>
                      <p className="text-base font-bold text-[#134168] mt-1">{formatDOP(booking.total_price)}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium shrink-0 ${
                      {
                        confirmed: 'bg-green-100 text-green-700',
                        pending: 'bg-yellow-100 text-yellow-700',
                        active: 'bg-teal-100 text-teal-700',
                        completed: 'bg-blue-100 text-blue-700',
                        cancelled: 'bg-gray-100 text-gray-700',
                        rejected: 'bg-red-100 text-red-700',
                      }[booking.status] || 'bg-gray-100 text-gray-700'
                    }`}>
                      {{
                        confirmed: t('providerDashboard.statusConfirmed'),
                        pending: t('providerDashboard.statusPending'),
                        active: t('providerDashboard.statusActive', 'En curso'),
                        completed: t('providerDashboard.statusCompleted', 'Completada'),
                        cancelled: t('providerDashboard.statusCancelled', 'Cancelada'),
                        rejected: t('providerDashboard.statusRejected', 'Rechazada'),
                      }[booking.status] || booking.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
